import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// The token stack has always named IBM Plex; until now nothing loaded it, so the
// console silently fell back to a system face. Self-host the weights the type
// scale uses (400/500/600) for both cuts — bundled by Vite, never a CDN, so the
// control plane stays offline-capable and within its content-security policy.
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import '@fontsource/ibm-plex-mono/600.css'
// The display voice: Chakra Petch is the one non-Plex face, used ONLY for the
// operational chrome — page titles, region eyebrows, the wordmark. Its squared,
// slightly technical letterforms read as an instrument label rather than a
// document heading, which is the whole point; prose stays IBM Plex Sans and
// machine values stay IBM Plex Mono (the load-bearing mono/sans split holds).
import '@fontsource/chakra-petch/500.css'
import '@fontsource/chakra-petch/600.css'
import '@fontsource/chakra-petch/700.css'
import '@/design-system/tokens.css'
import '@/design-system/base.css'
import '@/design-system/scrollbars.css'
import { App } from '@/app/App'
import { syncBrowserChrome } from '@/hooks/useTheme'

/**
 * Applies the stored appearance preferences before the first paint.
 *
 * `index.html` ships `data-theme="dark"` so the very first frame has a
 * background, and the shell's top bar re-applies the stored theme once it
 * mounts. Neither is enough on its own: an operator who chose the light theme
 * got a dark flash on every load, and the sign-in and setup screens — which
 * render before the shell exists — never applied the preference at all, so the
 * one screen a new operator sees first ignored the choice they had made.
 *
 * Reading the same two keys the theme and density controls write keeps this a
 * boot-time replay of a stored decision rather than a second source of truth.
 */
function applyStoredAppearance() {
  const theme = localStorage.getItem('kimene.theme')
  if (theme === 'light' || theme === 'dark') document.documentElement.dataset.theme = theme

  const density = localStorage.getItem('kimene.density')
  if (density === 'tight' || density === 'compact' || density === 'comfortable') {
    document.documentElement.dataset.density = density
  }

  // Written as a custom property rather than a data attribute, because the
  // readability floor does arithmetic on it. Bounded here as well as where it is
  // set: this reads a value a person can edit by hand, and an absurd one would
  // give every tile on the wall a minimum width no screen can satisfy.
  const font = Number.parseFloat(localStorage.getItem('kimene.termFont') ?? '')
  if (Number.isFinite(font) && font >= 9 && font <= 24) {
    document.documentElement.style.setProperty('--k-term-font', String(font))
  }
}

applyStoredAppearance()
// After the attribute is set, never before: this reads the resolved `--k-bg` off
// the document, which is the theme's own value rather than a second copy of it.
syncBrowserChrome()

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

/**
 * Registers the service worker, which is what lets a browser offer to install
 * this console as an application.
 *
 * Production only. In development Vite serves modules the worker has no business
 * standing in front of, and a worker registered against a dev server outlives it
 * — the next `pnpm dev` on the same origin is served by a worker from whichever
 * branch registered it.
 *
 * After first paint, because registration competes with the bundle for the same
 * connection and nothing on screen depends on it.
 *
 * Failure is swallowed on purpose: the console works without it. A worker is
 * refused on an insecure origin, under some enterprise policies, and in private
 * windows, and none of those is a reason to put an error in front of an
 * operator who did not ask to install anything.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {})
  })
}
